// MACHINERY V2 (Stroje) — the status-contract card's SHARED PIECES (split out of
// StateCard.tsx at M2.16b, step 1b).
//
// >>> WHY THIS FILE EXISTS. <<< StateCard.tsx stood at 292 of the project's
// 300-line cap and M2.16b's V1 work touches it. The ticket's near-cap rule makes
// the split a discrete FIRST task, before any feature line lands — the same rule
// that produced cardStateStyles.ts (M2.2), FleetContent.tsx (M2.13),
// FleetShell.tsx (M2.14) and useFleetSectionState.ts (M2.16a).
//
// THE SEAM: what OTHER surfaces consume versus what only the §4.3 composition
// does. StateCard.tsx must go on being the status contract and nothing else, so
// the two pieces that had already outgrown it move here:
//
//   · NoTelemetryFooter — the "there is no MachineConnection row" panel. It was
//     never StateCard-only: FleetCard.tsx and profile/ProfileLiveRibbon.tsx both
//     imported it FROM StateCard, i.e. they imported a page-level composition
//     file to reach one atom. M2.16b's compact V1 card is a fourth consumer of
//     the same VOCABULARY (it renders the strings, not the panel — see
//     FleetCardCompact.tsx's footer branch), which is what made the misplacement
//     worth fixing rather than living with.
//   · sinceClause — a pure formatter over (telemetry, derived status, prefix).
//     It decides no state and paints nothing.
//
// This is a MOVE, not a rewrite: both bodies are byte-identical to the block
// that sat at StateCard.tsx:45-114 before the split, and the three importers
// were repointed. Nothing about the rendering changed.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import StatusIcon from "./StatusIcon";
import { MachineryV2Status, MachineryV2Telemetry } from "./cardStates";

/** Footer for machines with no MachineConnection row. Dashed, quiet, faint —
 *  this is the absence of a thing, not a warning. */
export const NoTelemetryFooter: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        letterSpacing: "0.04em",
        color: "var(--faint)",
        borderTop: "1px dashed var(--border)",
        background: "transparent",
      }}
    >
      <span style={{ opacity: 0.7, display: "inline-flex" }}>
        <StatusIcon kind="cloudoff" size={11} />
      </span>
      <span style={{ textTransform: "uppercase" }}>
        {translate("myMachinery:v2.noTelemetry")}
      </span>
      <span
        style={{
          opacity: 0.6,
          letterSpacing: 0,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        · {translate("myMachinery:v2.noTelemetryHint")}
      </span>
    </div>
  );
};

/** "od 06:49 · 2h 14m" for running/idle, "od 23:18" for alarm, null when the
 *  adapter did not supply a start time. Never fabricates an elapsed value.
 *  `status` is the DERIVED badge status, not the raw telemetry field, and
 *  `prefix` is the whole prefix — the caller REPLACES it for last-known data
 *  rather than stacking a second word in front of it. */
export const sinceClause = (
  t: MachineryV2Telemetry,
  status: MachineryV2Status | null,
  prefix: string
): string | null => {
  if (!t.since || !status) return null;
  if (status === "running" || status === "idle") {
    return t.elapsed
      ? prefix + " " + t.since + " · " + t.elapsed
      : prefix + " " + t.since;
  }
  if (status === "alarm") return prefix + " " + t.since;
  return null;
};
